/**
 * /devhub/action-frames — ActionFrame ledger (operator). Surface Layer (/01).
 *
 * REQ-001.91 DevHub UI rebuild · Phase 2 (already-live domains). The live ActionFrame queue
 * (`/api/control/actionframes` via `useControlActionFrames`) rendered through the Phase-0 kit:
 * a clickable OperatorDataGrid; each row opens the OperatorDetailTearsheet. Read-only ledger view.
 * Operator-only (routePolicy plane guard + self-guard).
 */

import { InlineNotification } from '@carbon/react';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import type { OperatorColumn, OperatorDetailMode } from '@contracts/devhub-kit.contract';
import { useEntitlements } from '@lib/persistence/useEntitlements';
import { useControlActionFrames, type ControlFrameRow } from '@lib/persistence/useRuntimeData';
import { AiAffordanceLabel, OperatorDataGrid, OperatorDetailTearsheet } from '../components/devhub-kit';

export const Route = createFileRoute('/devhub/action-frames')({ component: DevHubActionFramesPage });

const COLUMNS: OperatorColumn<ControlFrameRow>[] = [
  { key: 'id', header: 'Frame' },
  { key: 'intent', header: 'Intent' },
  { key: 'status', header: 'Status', isStatus: true },
  { key: 'actor', header: 'Actor' },
  { key: 'risk_level', header: 'Risk' },
  { key: 'created_at', header: 'Created' },
];

function DevHubActionFramesPage() {
  const { isElevated } = useEntitlements();
  const { frames, isLoading } = useControlActionFrames(50);
  const [selected, setSelected] = useState<ControlFrameRow | null>(null);
  const [mode, setMode] = useState<OperatorDetailMode>('view');

  if (!isElevated) {
    return (
      <main className="view">
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title="Operator access required"
          subtitle="The ActionFrame ledger is operator-only."
        />
      </main>
    );
  }

  return (
    <main className="view">
      <header className="view__header">
        <h1 className="view__title">
          ActionFrames
          <AiAffordanceLabel />
        </h1>
        <p className="view__subtitle">Live queue — the operator-visible ActionFrame ledger.</p>
      </header>

      <section className="view__section">
        <OperatorDataGrid
          ariaLabel="ActionFrame ledger"
          columns={COLUMNS}
          rows={frames}
          getRowId={(row) => row.id}
          onRowClick={(row) => {
            setSelected(row);
            setMode('view');
          }}
          loading={isLoading}
          emptyLabel="No ActionFrames in the queue yet."
        />
      </section>

      <OperatorDetailTearsheet
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected?.id ?? ''}
        subtitle={selected?.intent ?? ''}
        mode={mode}
        onModeChange={setMode}
        fields={
          selected
            ? [
                { label: 'Intent', value: selected.intent },
                { label: 'Tool route', value: selected.tool_route },
                { label: 'Status', value: selected.status },
                { label: 'Actor', value: selected.actor },
                { label: 'Risk level', value: selected.risk_level },
                { label: 'Client frame id', value: selected.client_frame_id ?? '—' },
                { label: 'Requires approval', value: selected.requires_human_approval ? 'yes' : 'no' },
                { label: 'Created', value: selected.created_at },
              ]
            : []
        }
      />
    </main>
  );
}
